import advancedFormat from "dayjs/plugin/advancedFormat";
import Swal from "sweetalert2";
import dayjs from "dayjs";

import { FileInterface } from "../types/RecordInterfaces";
import { CloudPermissions } from "../hooks/useCloudPermissions";
import { FileSources } from "../types/FileInterfaces";
import { Tag } from "../types/GlobalInterfaces";
import TenantAPI from "../api/api/TenantAPI";
dayjs.extend(advancedFormat);

const primitives = ["boolean", "number", "string"];

// Ensures a functions exists, and it's type is a function.
export const callFunctionIfExists = (func: any, ...args: any[]) => {
  if (func && typeof func === "function") {
    return func(...args);
  }
};

export const generateModuleSettingsTabs = ({
  tenantID,
  moduleAPIName,
  isTemplate = false,
  templateID,
  permissions,
  isDynamic,
}: {
  tenantID: string;
  moduleAPIName: string;
  isTemplate: boolean;
  templateID: string;
  permissions?: CloudPermissions;
  isDynamic?: boolean;
}) => {
  let startUrl;

  if (isTemplate) {
    startUrl = `/supportal/global/templates/${templateID}/${moduleAPIName}/`;
  } else {
    startUrl = `/clouds/${tenantID}/settings/modules/manage/${moduleAPIName}/`;
  }

  if (isDynamic) {
    return [
      {
        title: "Query",
        link: startUrl + "query",
        activeValue: "query",
        hidden: permissions && !permissions.cloud_get_indexes,
      },
      {
        title: "Fields",
        link: startUrl + "fields",
        activeValue: "fields",
      },
      {
        title: "Sections & Layout",
        link: startUrl + "recorddetaillayout",
        activeValue: "recorddetaillayout",
      },
      {
        title: "Other Settings",
        link: startUrl + "settings",
        activeValue: "settings",
      },
    ];
  }

  return [
    {
      title: "Fields",
      link: startUrl + "fields",
      activeValue: "fields",
    },
    {
      title: "Sections & Layout",
      link: startUrl + "recorddetaillayout",
      activeValue: "recorddetaillayout",
    },
    {
      title: "Name",
      link: startUrl + "recordnametemplate",
      activeValue: "recordnametemplate",
    },
    {
      title: "Tags",
      link: startUrl + "recordtags",
      activeValue: "recordtags",
    },
    {
      title: "Indexes",
      link: startUrl + "indexes",
      activeValue: "indexes",
      hidden: permissions && !permissions.cloud_get_indexes,
    },
    {
      title: "Referenced By",
      link: startUrl + "referencedby",
      activeValue: "referencedby",
    },
    {
      title: "Other Settings",
      link: startUrl + "settings",
      activeValue: "settings",
    },
  ];
};

export async function getFileURL(
  fileName: string,
  expiration: number = 31536000,
  iSupportal?: boolean,
  tenantID?: string
): Promise<string> {
  try {
    const getUrlResult = await TenantAPI.getFileURL(
      fileName,
      expiration,
      false,
      iSupportal,
      tenantID
    );

    return getUrlResult?.data?.data?.url ? getUrlResult?.data?.data?.url : "";
  } catch (e) {
    return "";
  }
}

export function randomColor() {
  const randomInt = (min: number, max: number) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  return () => {
    const h = randomInt(0, 360);
    const s = randomInt(42, 98);
    const l = randomInt(40, 90);
    return `hsl(${h},${s}%,${l}%)`;
  };
}

export async function uploadImage(
  file: File,
  returnURL = true,
  returnBoth = false,
  usePublicContainer = false,
  sources: FileSources[] = []
): Promise<string | { fileName: string; url: string; files: FileInterface[] }> {
  let url = "";

  try {
    const uploadFileResult = await TenantAPI.uploadFile({
      file: file,
      usePublicContainer,
      sources,
    });

    const fileName = uploadFileResult.data.data.paths[0];

    if (!returnURL) {
      return fileName;
    }

    if (!usePublicContainer) {
      const getUrlResult = await TenantAPI.getFileURL(
        fileName,
        31536000,
        usePublicContainer
      );

      url = getUrlResult.data.data.url;
    } else {
      url = uploadFileResult.data.url;
    }

    if (returnBoth) {
      return { fileName, url, files: uploadFileResult.data.data.files };
    }
  } catch (err) {
    console.log(err);
  }

  return url;
}

export const pathnameFromURL = (url: string) => {
  if (!url) {
    return "";
  }

  const regex = /.*(?=uploaded_files)/;

  if (url.includes("uploaded_files")) {
    url = "https://app.growthcloud.com/" + url.replace(regex, "");
  }

  const url2 = new URL(url);

  if (url2.pathname.startsWith("/")) {
    return url2.pathname.substring(1);
  }

  return url2.pathname ? url2.pathname : "";
};

// Get error message from error object, otherwise return fallback message.
export const getErrorMessage = (error: any, fallbackMessage?: string) => {
  if (typeof error === "string") return error;

  if (error && typeof error === "string") return error;

  console.error(error);

  console.log(
    error?.response?.data?.message ||
      error?.message ||
      fallbackMessage ||
      "An unexpected error occurred."
  );

  return (
    error?.response?.data?.message ||
    error?.message ||
    fallbackMessage ||
    "An unexpected error occurred."
  );
};

export const groupTagData = (tags: Array<Tag>) => {
  const tagsData: { [key: string]: Array<Tag> } = {};
  if (!tags || !tags.length) return null;

  // Group tags based on TagGroup in the tag data.
  tags.forEach((tag: Tag) => {
    if (tag.TagGroup) {
      if (!tagsData[tag.TagGroup]) {
        tagsData[tag.TagGroup] = [];
      }

      tagsData[tag.TagGroup].push(tag);
    } else {
      if (!tagsData["No Group"]) {
        tagsData["No Group"] = [];
      }

      tagsData["No Group"].push(tag);
    }
  });

  return tagsData;
};

// Underscore didn't work for me so here is workaround
// TODO: Check the Typescript here
export const underscoreGet = <T extends Record<string, any>>(
  object: T,
  path: string | string[],
  defaultValue: T
): T => {
  let result = object;

  if (!Array.isArray(path)) {
    return object?.[path] ?? defaultValue;
  }

  for (let i = 0; i < path.length; i++) {
    if (result === undefined) break;

    const p = path[i];
    result = result?.[p];
  }

  return result ?? defaultValue;
};

// For deleting object properties provided an object and keys
export const deleteObjectProperties = (
  obj: { [key: string]: any },
  properties: string[]
) => {
  if (!obj || !properties || !properties.length) return obj;

  for (const property of properties) {
    delete obj[property];
  }
};

export const handleLocalNotifications = () => {
  let reloadedEvent: any = localStorage.getItem("reloaded");

  if (!reloadedEvent) return;

  localStorage.removeItem("reloaded");

  reloadedEvent = JSON.parse(reloadedEvent);

  if (reloadedEvent?.Type === "success") {
    Swal.fire({
      title: "Restoration Complete",
      text: `We successfully restored your Cloud from ${dayjs(
        reloadedEvent.Date
      ).format("MMMM Do YYYY, h:mm:ss a")}.`,
      icon: "success",
      confirmButtonColor: "var(--primary)",
    });
  }
};

// For getting map value, regardless of object or map type
export const getMapValue = <T>(
  map: Map<string, T> | { [key: string]: T },
  key: string
) => {
  const isMap = map instanceof Map;
  const isObject = typeof map === "object" && map !== null;

  if (isMap) {
    return map.get(key);
  } else if (isObject) {
    return map[key];
  } else {
    return undefined;
  }
};

// The number of keys an object has, including nested keys.
export function countKeys(obj: { [key: string]: any }): number {
  return Object.keys(obj).reduce((total, currentKey) => {
    // Check if the current key points to an object
    if (typeof obj[currentKey] === "object" && obj[currentKey] !== null) {
      // If it is an object, recursively call this function
      // to count the number of keys in the nested object
      return total + countKeys(obj[currentKey]);
    } else {
      // If it is not an object, increment the total
      return total + 1;
    }
  }, 0);
}

export function hasKeys(obj: any, keysToCheck: Array<string> | null): boolean {
  try {
    const keys = Object.keys(obj);

    if (keys.length === 0) return false;

    if (!keysToCheck) return true;

    for (const k of keysToCheck) {
      if (!keys.includes(k)) return false;
    }

    return true;
  } catch {
    return false;
  }
}

export function isObject(obj: any) {
  return obj !== null && typeof obj === "object" && !Array.isArray(obj);
}

export function safeObjectMerge(obj1: any, obj2: any) {
  const object1 = isObject(obj1) ? obj1 : {};
  const object2 = isObject(obj2) ? obj2 : {};

  return { ...object1, ...object2 };
}

// Compares to objects (That should have the same keys) and returns the changed key/value pairs
// Obj1 - The original object
// Obj2 - The object with changed values
export const getChangedObjectValues = (
  obj1: any,
  obj2: any,
  ignoreNonPrimatives = false
) => {
  if (obj1 && typeof obj1 === "object" && (!obj2 || typeof obj2 !== "object")) {
    return { ...obj1 };
  }
  if (obj2 && typeof obj2 === "object" && (!obj1 || typeof obj1 !== "object")) {
    return { ...obj2 };
  }
  if (
    (!obj1 && !obj2) ||
    (typeof obj1 !== "object" && typeof obj2 === "object")
  ) {
    return {};
  }

  const finalObj: any = {};

  const keys = Object.keys(obj1);

  if (!keys.length) return finalObj;

  for (const key of keys) {
    if (ignoreNonPrimatives && !primitives.includes(typeof obj1[key])) continue;

    if (obj1[key] !== obj2[key]) {
      finalObj[key] = obj2[key];
    }
  }

  return finalObj;
};

export const convertArrayToUniquePropertyArray = (
  arr: any[],
  propertyName: string
) => {
  const uniqueValues: string[] = [];

  for (let i = 0; i < arr.length; i++) {
    const value = arr[i][propertyName];
    if (!uniqueValues.includes(value)) {
      uniqueValues.push(value);
    }
  }

  return uniqueValues;
};
